import { FC, useEffect, useState } from 'react';
import styled, { CSSObject } from '@emotion/styled';
import { CancelToken } from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../store';
import { getTrainingVersionList, selectTrainingVersionList, trainingVersionListActions } from '../../store/entities/trainingVersionList';
import { QcsButton } from '../common/basic/QcsButton';
import { TableRowButton } from '../common/basic/TableRowButton';
import { QcsTypography } from '../common/basic/QcsTypography';
import { CellDate } from '../common/grid/CellDate';
import { Grid } from '../common/grid/Grid';
import { QcsTableCell } from '../common/basic/QcsTableCell';
import { FaIcon } from '../common/FaIcon';
import { faCopy } from '@fortawesome/free-solid-svg-icons';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { CopyVersionModal } from './CopyVersionModal';
import { CopyVersionToTrainingModal } from './CopyVersionToTrainingModal';
import { trainingListActions } from '../../store/entities/trainingList';
import { useTheme } from '@mui/material';
import { faCalendarCheck } from '@fortawesome/free-regular-svg-icons';
import { TrainingVersionDto } from '@api/models/training/TrainingVersionDto';
import { trainingVersionService } from '@api/services/services';

const HeaderContainer = styled.div(
  (): CSSObject => ({
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '1rem',
  }),
);

export const TrainingVersion: FC = () => {
  const { t } = useTranslation();
  const trainingVersionList = useAppSelector(selectTrainingVersionList);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const { trainingId } = useParams();
  const [versionToCopy, setVersionToCopy] = useState<TrainingVersionDto | null>(null);
  const [versionToCopyToTraining, setVersionToCopyToTraining] = useState<TrainingVersionDto | null>(null);

  useEffect(() => {
    dispatch(trainingVersionListActions.reload(true));
  }, [dispatch]);

  const handleGetData = (cancelToken: CancelToken) => {
    dispatch(getTrainingVersionList(trainingId!, cancelToken));
  };

  const handleDeleteVersion = (id: string) => async () => {
    await trainingVersionService.deleteById(id);
    handleReloadTrainingVersionList();
  };

  const handleAddVersionCopy = (version: TrainingVersionDto) => () => {
    setVersionToCopy(version);
  };

  const handleAddVersionCopyToTraining = (version: TrainingVersionDto) => () => {
    setVersionToCopyToTraining(version);
  };

  const handleRenderData = (item: TrainingVersionDto) => {
    return (
      <>
        <QcsTableCell>{item.active && <FaIcon icon={faCalendarCheck} color={theme.palette.primary.main} />}</QcsTableCell>
        <CellDate value={item.validFrom} />
        <CellDate value={item.validTo} />
        <QcsTableCell align="center">
          <TableRowButton onClick={handleAddVersionCopy(item)}>
            <FaIcon icon={faCopy} />
          </TableRowButton>
        </QcsTableCell>
        <QcsTableCell align="center">
          <TableRowButton onClick={handleAddVersionCopyToTraining(item)}>
            <FaIcon icon={faCopy} />
          </TableRowButton>
        </QcsTableCell>
        <QcsTableCell align="center">
          <TableRowButton disabled={!item.editable} onClick={handleDeleteVersion(item.id!)}>
            <DeleteForeverIcon color={!item.editable ? undefined : 'error'} />
          </TableRowButton>
        </QcsTableCell>
      </>
    );
  };

  const handleReloadTrainingVersionList = () => {
    dispatch(trainingVersionListActions.reload(true));
    dispatch(trainingListActions.reload(true));
  };

  const handleRowClick = (item: TrainingVersionDto) => {
    navigate(`/training/${trainingId}/${item.id}`);
  };

  const handleAddClick = () => {
    navigate(`/training/${trainingId}/new`);
  };

  return (
    <>
      <CopyVersionModal
        versionToCopy={versionToCopy}
        onClose={() => setVersionToCopy(null)}
        reloadTrainingVersionList={handleReloadTrainingVersionList}
      />
      <CopyVersionToTrainingModal
        versionToCopy={versionToCopyToTraining}
        onClose={() => setVersionToCopyToTraining(null)}
        reloadTrainingVersionList={handleReloadTrainingVersionList}
      />
      <HeaderContainer>
        <QcsTypography variant="h5">{t('training.version')}</QcsTypography>
        <QcsButton variant="contained" onClick={handleAddClick}>
          {t('training.addVersion')}
        </QcsButton>
      </HeaderContainer>
      <Grid<TrainingVersionDto>
        headers={[
          { sx: { width: '56px' } },
          { captionStr: 'training.validFrom', orderName: 'validFrom' },
          { captionStr: 'training.validTo', orderName: 'validTo' },
          {
            captionStr: 'training.copy.addVersion',
            sx: { textAlign: 'center' },
          },
          {
            captionStr: 'training.copy.versionToTraining',
            sx: { textAlign: 'center' },
          },
          {
            captionStr: 'training.deleteVersion',
            sx: { textAlign: 'center' },
          },
        ]}
        data={trainingVersionList}
        gridActions={trainingVersionListActions}
        renderData={handleRenderData}
        onRowClick={handleRowClick}
        getData={handleGetData}
        search={false}
      />
    </>
  );
};
